import { useState } from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { Upload, Image as ImageIcon, Loader2 } from "lucide-react"
import { UploadImageInput, useUploadImage } from "@/graphql/hooks/useUploadImage"
import { useAuthContext } from "@/contexts/AuthContext"

interface CoverUploadProps {
  src?: string
  name: string
  type: UploadImageInput["type"]
  personId?: number
  tournamentId?: number
  className?: string
  cb?: () => unknown
}

export const CoverUpload = ({
  src,
  name,
  tournamentId = 0,
  type,
  personId,
  className,
  cb,
}: CoverUploadProps) => {
  const { isAdmin, isAuthenticated } = useAuthContext()
  const [preview, setPreview] = useState<string | null>(null)
  const { uploadImageUser, loading } = useUploadImage()

  const convertToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        resolve(result.split(",")[1])
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      e.preventDefault()
      return
    }

    const validTypes = ["image/jpeg", "image/jpg", "image/png"]
    if (!validTypes.includes(file.type)) {
      alert("Solo se permiten archivos JPG y PNG")
      e.target.value = ""
      return
    }

    const maxSize = 2 * 1024 * 1024 // 2MB para cover
    if (file.size > maxSize) {
      alert("El archivo no debe exceder 2MB")
      e.target.value = ""
      return
    }

    try {
      const previewUrl = URL.createObjectURL(file)
      setPreview(previewUrl)

      const base64 = await convertToBase64(file)
      await uploadImageUser({
        personId,
        image: base64,
        name: file.name,
        type,
        tournamentId,
      })

      cb?.()
    } catch (err) {
      console.error("Error uploading cover:", err)
      setPreview(null)
    } finally {
      e.target.value = ""
    }
  }

  const displaySrc = preview || src

  return (
    <div className={cn("flex flex-col gap-3 w-full relative", className)}>
      <label
        className={cn(
          "relative flex items-center justify-center rounded-lg cursor-pointer overflow-hidden",
          "sm:h-96 w-full bg-gradient-to-r from-primary/10 to-accent/10 hover:bg-muted/30 transition",
          "dark:from-primary/20 dark:to-accent/20 dark:hover:bg-gray-700/50",
        )}
      >
        {displaySrc ? (
          <img
            src={displaySrc}
            alt={name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none"
            }}
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-gray-500 dark:text-gray-300">
            <ImageIcon className="w-10 h-10" />
            <span className="text-sm font-medium">Haz clic para subir cover</span>
            <span className="text-xs">(JPG o PNG, máx 2MB)</span>
          </div>
        )}

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 dark:bg-black/50">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          </div>
        )}

        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleFileChange}
          className="sr-only"
        />
      </label>

      <label className="self-start absolute top-2 right-2">
        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleFileChange}
          className="sr-only"
        />
        <Button type="button" disabled={loading} asChild>
          <span className="flex items-center gap-2">
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {displaySrc ? "Cambiar cover" : "Subir cover"}
          </span>
        </Button>
      </label>
    </div>
  )
}
